import { useEffect, useRef, useState } from "react";
import { collection, addDoc, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { track } from "../analytics";
import "./Shop.css";

const AMOUNT_PAISE = 29900;

const SALE_KEY = "brandorah_sale_end";
const SALE_DURATION = 3 * 60 * 60 * 1000;

const RESTART_DELAY = 10 * 60 * 1000; // 10 min pause before restarting

function SaleTimer() {
  const [endTime, setEndTime] = useState(() => {
    try {
      const stored = localStorage.getItem(SALE_KEY);
      if (stored) {
        const t = parseInt(stored, 10);
        if (!isNaN(t) && t > Date.now()) return t;
      }
    } catch {}
    const end = Date.now() + SALE_DURATION;
    try { localStorage.setItem(SALE_KEY, String(end)); } catch {}
    return end;
  });

  const [timeLeft, setTimeLeft] = useState(() => Math.max(0, endTime - Date.now()));
  const pendingRestart = useRef(false);

  useEffect(() => {
    // Immediately sync timeLeft when endTime resets after restart
    setTimeLeft(Math.max(0, endTime - Date.now()));

    const id = setInterval(() => {
      const remaining = Math.max(0, endTime - Date.now());
      setTimeLeft(remaining);

      if (remaining === 0 && !pendingRestart.current) {
        pendingRestart.current = true;
        setTimeout(() => {
          const newEnd = Date.now() + SALE_DURATION;
          try { localStorage.setItem(SALE_KEY, String(newEnd)); } catch {}
          setEndTime(newEnd);
          pendingRestart.current = false;
        }, RESTART_DELAY);
      }
    }, 1000);

    return () => clearInterval(id);
  }, [endTime]);

  if (timeLeft <= 0) return (
    <div className="sale-banner sale-banner--soon">
      <span className="sale-banner__label">🔥 Flash Sale</span>
      <span className="sale-banner__dot">·</span>
      <span className="sale-banner__soon">Next sale starting soon. Don't miss it</span>
    </div>
  );

  const h = Math.floor(timeLeft / 3_600_000);
  const m = Math.floor((timeLeft % 3_600_000) / 60_000);
  const s = Math.floor((timeLeft % 60_000) / 1_000);
  const pad = (n) => String(n).padStart(2, "0");

  return (
    <div className="sale-banner">
      <span className="sale-banner__label">⚡ Flash Sale</span>
      <span className="sale-banner__dot">·</span>
      <span className="sale-banner__msg">Offer ends in</span>
      <div className="sale-banner__timer">
        <span className="sale-banner__seg">{pad(h)}<em>h</em></span>
        <span className="sale-banner__colon">:</span>
        <span className="sale-banner__seg">{pad(m)}<em>m</em></span>
        <span className="sale-banner__colon">:</span>
        <span className="sale-banner__seg">{pad(s)}<em>s</em></span>
      </div>
    </div>
  );
}
const API_BASE =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5000/api"
    : "/.netlify/functions";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

function EmailModal({ onProceed, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) { setError("Please enter your name."); return; }
    if (!/@gmail\.com$/i.test(email.trim())) { setError("Please enter a Gmail address (e.g. you@gmail.com) to get direct access to the files."); return; }
    setError("");
    setLoading(true);
    await onProceed(name.trim(), email.trim().toLowerCase());
    setLoading(false);
  }

  return (
    <div className="em-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="em-card">
        <button className="em-close" onClick={onClose}>✕</button>
        <div className="em-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0A0A12" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
            <polyline points="22,6 12,13 2,6"/>
          </svg>
        </div>
        <h2 className="em-heading">Where should we send your files?</h2>
        <p className="em-sub">Enter your Gmail address to get instant access to the design folder after payment.</p>
        <form onSubmit={handleSubmit} noValidate>
          <input
            className="em-input"
            type="text"
            placeholder="Your name"
            value={name}
            onChange={(e) => { setName(e.target.value); setError(""); }}
            autoFocus
            style={{ marginBottom: 10 }}
          />
          <input
            className={`em-input${error ? " em-input--err" : ""}`}
            type="email"
            placeholder="you@gmail.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(""); }}
          />
          {error && <p className="em-error">{error}</p>}
          <button className="em-btn" type="submit" disabled={loading}>
            {loading ? "Please wait…" : "Pay ₹299 →"}
          </button>
        </form>
        <p className="em-note">Gmail required for direct folder access. No spam.</p>
      </div>
    </div>
  );
}

function ProcessingPayment() {
  const STEPS = [
    "Verifying your payment...",
    "Granting access to your design files...",
    "Sending confirmation to your email...",
  ];
  const [step, setStep] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setStep(1), 2500);
    const t2 = setTimeout(() => setStep(2), 5000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <div className="proc-wrap">
      <div className="proc-logo">BRAND<em>ORAH</em></div>
      <div className="proc-body">
        <div className="proc-ring" />
        <p className="proc-title">Processing your order</p>
        <p className="proc-step" key={step}>{STEPS[step]}</p>
        <div className="proc-dots">
          {STEPS.map((_, i) => (
            <span key={i} className={`proc-dot${i === step ? " proc-dot--active" : i < step ? " proc-dot--done" : ""}`} />
          ))}
        </div>
        <p className="proc-note">Do not close or refresh this page</p>
      </div>
    </div>
  );
}

const SUPPORT_API =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5000/api/submit-query"
    : "/.netlify/functions/submit-query";

function SupportForm() {
  const [form, setForm] = useState({ name: "", email: "", issue: "Payment done but didn't receive files", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setStatus("sending");
    try {
      const res = await fetch(SUPPORT_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) { setStatus("sent"); }
      else throw new Error();
    } catch {
      setStatus("error");
    }
  }

  if (status === "sent") return (
    <div className="support-sent">
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#FFB300" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
      <p>Message sent. We'll get back to you within a few hours.</p>
    </div>
  );

  return (
    <form className="support-form" onSubmit={handleSubmit} noValidate>
      <div className="support-row">
        <input className="support-input" type="text" placeholder="Your name" value={form.name} onChange={set("name")} required />
        <input className="support-input" type="email" placeholder="your@gmail.com" value={form.email} onChange={set("email")} required />
      </div>
      <select className="support-input support-select" value={form.issue} onChange={set("issue")}>
        <option>Payment done but didn't receive files</option>
        <option>Download link not working</option>
        <option>Files not opening</option>
        <option>Wrong email entered</option>
        <option>Other</option>
      </select>
      <textarea className="support-input support-textarea" placeholder="Describe your issue..." value={form.message} onChange={set("message")} required rows={4} />
      {status === "error" && <p className="support-error">Something went wrong. Email us directly at mail@brandorah.com</p>}
      <button className="s-btn s-btn--gold" type="submit" disabled={status === "sending"}>
        {status === "sending" ? "Sending…" : "Send Message"}
      </button>
    </form>
  );
}

function PaymentSuccess({ paymentId, email, name }) {
  return (
    <div className="ps-wrap">
      <div className="ps-card">
        <div className="ps-icon-ring">
          <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#0A0A12" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h1 className="ps-heading">Payment Successful</h1>
        <p className="ps-sub">Thanks {name}! Your order is confirmed. We've granted your Gmail direct access to the design folder. Check your Google Drive under <strong>Shared with me</strong>.</p>
        <div className="ps-detail">
          <span className="ps-detail__label">Sent to</span>
          <span className="ps-detail__val">{email}</span>
        </div>
        <div className="ps-detail" style={{ marginTop: 8 }}>
          <span className="ps-detail__label">Payment ID</span>
          <span className="ps-detail__val">{paymentId}</span>
        </div>
        <div className="ps-items">
          {["50,000+ design files", "8 formats: PNG, EPS, AI, SVG & more", "Full commercial license", "Lifetime access"].map((t) => (
            <div key={t} className="ps-item">
              <span className="ps-item__tick">✓</span>{t}
            </div>
          ))}
        </div>
        <p className="ps-footer">Need help? <a href="mailto:mail@brandorah.com">mail@brandorah.com</a></p>
      </div>
    </div>
  );
}

const MARQUEE = [
  "Streetwear", "Typography", "Illustration", "Vintage",
  "Abstract", "Bold Graphics", "Motivational", "Dark Art", "Minimal", "POD Ready",
];

const GALLERY = [
  "/designs/hustler.png",
  "/designs/rebel-angel.png",
  "/designs/joker.png",
  "/designs/black-wolf-samurai.png",
  "/designs/cyberwolf.png",
  "/designs/eternal-battle.png",
  "/designs/butterfly-aesthetic.png",
  "/designs/stay-focused.png",
  "/designs/sport-cars.png",
  "/designs/tiger-leap.png",
  "/designs/vampire-cyberpunk.png",
  "/designs/samurai-assassin.png",
  "/designs/thunder-strike.png",
  "/designs/madclown.png",
  "/designs/streetwear-1.png",
  "/designs/glory-eternal.png",
  "/designs/seize-the-day.png",
  "/designs/powerful.png",
  "/designs/today-awesome.png",
  "/designs/let-hustle-speak.png",
];

const STACK_CARDS = [
  { img: "/mockups/mockup-crimson.webp",   alt: "Crimson graphic tee" },
  { img: "/mockups/mockup-abstract.webp",  alt: "Abstract streetwear tee" },
  { img: "/mockups/mockup-skull.webp",     alt: "Sugar skull tee" },
  { img: "/mockups/mockup-himalaya.webp",  alt: "Mountain graphic tee" },
];

const INCLUDES = [
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
        <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
      </svg>
    ),
    count: "50,000+", title: "Unique Designs",
    desc: "Streetwear, typography, illustration, vintage, abstract and every major style covered.",
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
      </svg>
    ),
    count: "8 Formats", title: "Every File Type",
    desc: "PNG, EPS, AI, SVG, CDR, DXF, PSD and PDF. Use the right format for any printer or platform.",
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        <polyline points="9 12 11 14 15 10"/>
      </svg>
    ),
    count: "100%", title: "Commercial License",
    desc: "Sell unlimited products on Printful, Printify, Redbubble, Etsy or your own store.",
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
      </svg>
    ),
    count: "1-Click", title: "Instant Access",
    desc: "Download link delivered to your email within seconds of payment.",
  },
];

function useViewerCount() {
  const [count, setCount] = useState(() => Math.floor(Math.random() * 21) + 5);
  useEffect(() => {
    const id = setInterval(() => {
      setCount((c) => Math.min(25, Math.max(5, c + (Math.random() < 0.5 ? 1 : -1) * Math.ceil(Math.random() * 2))));
    }, 28000);
    return () => clearInterval(id);
  }, []);
  return count;
}

export default function Shop() {
  const viewers = useViewerCount();
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    const link = document.querySelector("link[rel='icon']");
    const prev = link ? link.href : null;
    if (link) {
      link.type = "image/svg+xml";
      link.href = "/favicon.svg";
    }
    return () => {
      if (link && prev) {
        link.type = "image/png";
        link.href = prev;
      }
    };
  }, []);

  function handleBuy() {
    track("buy_clicked");
    setShowEmailModal(true);
  }

  async function proceedWithPayment(name, email) {
    track("email_submitted");
    const loaded = await loadRazorpayScript();
    if (!loaded) {
      alert("Payment service failed to load. Please check your connection and try again.");
      return;
    }

    // Save lead immediately on email submit
    let docRef = null;
    try {
      docRef = await addDoc(collection(db, "shop_orders"), {
        name,
        email,
        amount: AMOUNT_PAISE / 100,
        status: "pending",
        createdAt: serverTimestamp(),
      });
    } catch (fbErr) {
      console.error("Firebase lead save failed:", fbErr);
    }

    let orderData;
    try {
      const res = await fetch(`${API_BASE}/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: AMOUNT_PAISE }),
      });
      if (!res.ok) throw new Error("Order creation failed");
      orderData = await res.json();
    } catch {
      alert("Could not initiate payment. Please try again.");
      return;
    }

    setShowEmailModal(false);

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: orderData.amount,
      currency: orderData.currency,
      name: "Brandorah",
      description: "50,000+ T-Shirt Design Pack",
      order_id: orderData.order_id,
      handler: async (response) => {
        setProcessingPayment(true);
        try {
          const verifyRes = await fetch(`${API_BASE}/verify-payment`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }),
          });
          const data = await verifyRes.json();
          if (data.success) {
            try {
              if (docRef) {
                await updateDoc(doc(db, "shop_orders", docRef.id), {
                  paymentId: response.razorpay_payment_id,
                  orderId: response.razorpay_order_id,
                  status: "paid",
                });
              }
            } catch (fbErr) {
              console.error("Firebase update failed:", fbErr);
            }
            try {
              await fetch(`${API_BASE}/send-order-email`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name, email }),
              });
            } catch (mailErr) {
              console.error("Email send failed:", mailErr);
            }
            track("purchase", { currency: "INR", value: AMOUNT_PAISE / 100, transaction_id: response.razorpay_payment_id });
            setProcessingPayment(false);
            setPaymentSuccess({ paymentId: response.razorpay_payment_id, email, name });
          } else {
            setProcessingPayment(false);
            alert("Payment verification failed. Contact support at mail@brandorah.com");
          }
        } catch {
          setProcessingPayment(false);
          alert("Payment received but verification failed. Contact support with your payment ID: " + response.razorpay_payment_id);
        }
      },
      prefill: { name, email },
      notes: { name, email },
      theme: { color: "#FFB300" },
      modal: { ondismiss: () => track("payment_dismissed") },
    };

    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (response) => {
      track("payment_failed", { reason: response.error.description });
      alert("Payment failed: " + response.error.description);
    });
    track("payment_started");
    rzp.open();
  }

  useEffect(() => {
    // Page title + favicon for shop subdomain
    const prevTitle   = document.title;
    const faviconEl   = document.querySelector("link[rel='icon']");
    const prevFavicon = faviconEl ? faviconEl.href : null;

    document.title = "Brandorah | 50,000+ Print-Ready T-Shirt Designs";
    track("shop_viewed");

    const shopFavicon = `data:image/svg+xml,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
        <rect width="32" height="32" fill="#000"/>
        <text x="16" y="23" font-family="Georgia,serif" font-size="22" font-weight="bold" fill="#fff" text-anchor="middle">D</text>
      </svg>`
    )}`;
    if (faviconEl) faviconEl.href = shopFavicon;

    // FAQ accordion
    const handler = (e) => {
      const btn = e.target.closest(".faq-q");
      if (!btn) return;
      const item = btn.closest(".faq-item");
      const isOpen = item.classList.contains("open");
      document.querySelectorAll(".faq-item.open").forEach((i) => i.classList.remove("open"));
      if (!isOpen) item.classList.add("open");
    };
    document.addEventListener("click", handler);
    return () => {
      document.removeEventListener("click", handler);
      document.title = prevTitle;
      if (faviconEl && prevFavicon) faviconEl.href = prevFavicon;
    };
  }, []);

  if (processingPayment) return <ProcessingPayment />;
  if (paymentSuccess) return <PaymentSuccess paymentId={paymentSuccess.paymentId} email={paymentSuccess.email} name={paymentSuccess.name} />;

  return (
    <div className="shop">

      <SaleTimer />

      {showEmailModal && (
        <EmailModal
          onProceed={proceedWithPayment}
          onClose={() => setShowEmailModal(false)}
        />
      )}

      {/* NAV */}
      <nav className="s-nav">
        <div className="s-wrap s-nav__inner">
          <div className="s-nav__logo">BRAND<em>ORAH</em></div>
          <div className="s-nav__right">
            <a href="#includes" className="s-nav__link">What's Inside</a>
            <a href="#pricing"  className="s-nav__link">Pricing</a>
            <button className="s-btn s-btn--gold s-btn--sm" onClick={handleBuy}>Get Access - ₹299</button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="s-hero">
        <div className="s-hero__glow" />
        <div className="s-wrap">
          <div className="s-hero__grid">

            <div className="s-hero__copy">
              <div className="s-hero__pill">
                <span className="s-dot" />
                Instant Digital Delivery
              </div>
              <h1 className="s-hero__h1">
                50,000+ T-Shirt<br />
                <em>Designs.</em>
              </h1>
              <p className="s-hero__tagline">Print-ready. One download.</p>
              <p className="s-hero__sub">
                PNG, EPS, AI, SVG, CDR, DXF and PSD files for clothing brands, POD sellers and print shops.
                Works with Printful, Printify, Redbubble and screen printing. Zero design skills needed.
              </p>
              <div className="s-hero__actions">
                <button className="s-btn s-btn--gold s-btn--xl" onClick={handleBuy}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Get Instant Access - ₹299
                </button>
                <a href="#gallery" className="s-btn s-btn--ghost">See designs ↓</a>
              </div>
              <p className="s-hero__hint">One-time payment · <strong>No subscription</strong> · Keep the files forever</p>
              <div className="s-viewers">
                <span className="s-viewers__dot" />
                <span>{viewers} people viewing this right now</span>
              </div>
              <div className="s-hero__stats">
                <div className="s-stat"><strong>50,000+</strong><span>design files</span></div>
                <div className="s-stat__div" />
                <div className="s-stat"><strong>8 Formats</strong><span>PNG · EPS · AI · SVG · more</span></div>
                <div className="s-stat__div" />
                <div className="s-stat"><strong>Instant</strong><span>email delivery</span></div>
              </div>
            </div>

            <div className="s-hero__visual">
              <div className="s-stack">
                {STACK_CARDS.map((c, i) => (
                  <div key={i} className={`s-stack__card s-stack__card--${i + 1}`}>
                    <img src={c.img} alt={c.alt} className="s-stack__img" />
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* MOBILE MOCKUP STRIP */}
      <div className="s-mockup-strip">
        {STACK_CARDS.map((c, i) => (
          <div key={i} className="s-mockup-strip__item">
            <img src={c.img} alt={c.alt} />
          </div>
        ))}
      </div>

      {/* MARQUEE */}
      <div className="s-marquee-wrap">
        <div className="s-marquee">
          {[0, 1].map((k) => (
            <div key={k} className="s-marquee__track" aria-hidden={k === 1}>
              {MARQUEE.map((t) => <span key={t} className="s-marquee__item">{t}</span>)}
            </div>
          ))}
        </div>
      </div>

      {/* INCLUDES */}
      <section id="includes" className="s-section">
        <div className="s-wrap">
          <p className="s-eyebrow">What's inside</p>
          <h2 className="s-h2 s-h2--wide">Everything you need to launch your clothing line today.</h2>
          <div className="s-inc-grid">
            {INCLUDES.map((c) => (
              <div key={c.title} className="s-inc-card">
                <div className="s-inc-card__icon">{c.icon}</div>
                <div className="s-inc-card__count">{c.count}</div>
                <div className="s-inc-card__title">{c.title}</div>
                <div className="s-inc-card__desc">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="s-section s-section--flush-top">
        <div className="s-wrap">
          <p className="s-eyebrow">Design preview</p>
          <h2 className="s-h2">A look inside the collection.</h2>
          <p className="s-muted-text">50,000+ designs across every style. A small sample below.</p>
          <div className="s-gallery">
            {GALLERY.map((src, i) => (
              <div key={i} className="s-g">
                <img
                  src={src}
                  alt="Design preview"
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="s-section s-section--raised">
        <div className="s-wrap">
          <p className="s-eyebrow s-center">How it works</p>
          <h2 className="s-h2 s-center">Three steps. Under a minute.</h2>
          <div className="s-how">
            {[
              { n: "01", title: 'Click "Get Instant Access"', desc: "Proceed to secure Razorpay checkout. One-time payment of ₹299. No hidden fees, no recurring charges." },
              { n: "02", title: "Payment confirmed",          desc: "Razorpay processes your payment securely and confirms your order instantly." },
              { n: "03", title: "Files in your inbox",        desc: "A download link lands in your email within seconds. Access all 50,000+ files, forever." },
            ].map((s) => (
              <div key={s.n} className="s-how__step">
                <div className="s-how__num">{s.n}</div>
                <div className="s-how__title">{s.title}</div>
                <div className="s-how__desc">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="s-section s-section--flush-top">
        <div className="s-wrap">
          <p className="s-eyebrow">What customers say</p>
          <h2 className="s-h2 s-h2--wide">Real buyers. Real results.</h2>
          <div className="s-testimonials">
            {[
              { name: "Rahul M.", city: "Surat", avatar: "R", text: "Bhai 299 mein itna milega socha nahi tha. Already printed 3 designs and sold them on Instagram. Paise vasool in 2 days." },
              { name: "Priya K.", city: "Bengaluru", avatar: "P", text: "I run a small merch store. This pack saved me literally months of design work. The PNG quality is top notch. Clients are happy." },
              { name: "Deepak S.", city: "Jaipur", avatar: "D", text: "Tried a few free bundles before but the quality was bad. This one is different. Clean files, print-ready. No complaints at all." },
              { name: "Sneha R.", city: "Mumbai", avatar: "S", text: "Ordered for my college fest tshirts. Designs are so good. Google Drive access worked instantly after payment. Super smooth experience." },
              { name: "Arjun T.", city: "Hyderabad", avatar: "A", text: "Running a print shop. ₹299 is nothing compared to what designers charge per design. This is a goldmine honestly." },
              { name: "Meera V.", city: "Pune", avatar: "M", text: "Was sceptical at first but took the risk. 100% worth it. Already used 20+ designs for custom orders. Very satisfied." },
            ].map((t) => (
              <div key={t.name} className="s-tcard">
                <div className="s-tcard__stars">★★★★★</div>
                <p className="s-tcard__text">"{t.text}"</p>
                <div className="s-tcard__author">
                  <div className="s-tcard__avatar">{t.avatar}</div>
                  <div>
                    <div className="s-tcard__name">{t.name}</div>
                    <div className="s-tcard__city">{t.city}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="s-section">
        <div className="s-wrap">
          <p className="s-eyebrow">Pricing</p>
          <div className="s-pricing">
            <div className="s-pricing__left">
              <h3 className="s-h2 s-pricing__h">Everything included.<br />One price.</h3>
              <p className="s-muted-text s-pricing__intro">
                No upsells. No subscription. Pay once, use the designs forever across every product you ever sell.
              </p>
              <div className="s-feat-list">
                {[
                  ["50,000+ design files",           "Streetwear, typography, illustration, vintage & more"],
                  ["8 formats included",             "PNG, EPS, AI, SVG, CDR, DXF, PSD. Works with any printer or platform"],
                  ["Full commercial license",        "Sell on Printful, Printify, Etsy, your own store. Unlimited use."],
                  ["Instant digital delivery",        "Download link in your inbox seconds after payment"],
                  ["Lifetime access, no expiry",     "Yours to keep, reuse and build with forever"],
                ].map(([title, sub]) => (
                  <div key={title} className="s-feat">
                    <div className="s-feat__chk">✓</div>
                    <div className="s-feat__text">{title}<span>{sub}</span></div>
                  </div>
                ))}
              </div>
            </div>

            <div className="s-price-card">
              <div className="s-price-card__shine" />
              <div className="s-price-card__pill">⚡ Limited Offer</div>
              <div className="s-price-card__name">Ultimate T-Shirt Design Pack</div>
              <div className="s-price-card__tagline">50,000+ designs · Instant download · Commercial use</div>
              <div className="s-price-card__amount">₹299</div>
              <div className="s-price-card__was">₹2,999</div>
              <div className="s-price-card__save">↑ You save ₹2,700 (90% off)</div>
              <div className="s-price-card__items">
                {["50,000+ design files (8 formats)", "PNG, EPS, AI, SVG, CDR, DXF, PSD", "Commercial license", "Instant email delivery", "Lifetime access"].map((t) => (
                  <div key={t} className="s-price-card__item"><span>✓</span>{t}</div>
                ))}
              </div>
              <button className="s-btn s-btn--gold s-btn--full" onClick={handleBuy}>
                Get Instant Access →
              </button>
              <div className="s-price-card__note">
                🔒 Secured by Razorpay · One-time · No hidden charges
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="s-section s-section--flush-top">
        <div className="s-wrap">
          <p className="s-eyebrow s-center">FAQ</p>
          <h2 className="s-h2 s-center">Common questions.</h2>
          <div className="s-faq">
            {[
              ["Is this a physical product?",
               "No. This is a digital download. You receive a download link with all 50,000+ design files across 8 formats. Nothing is shipped."],
              ["How do I receive my files?",
               "After payment, a download link is emailed to the address you used at checkout. Click it to access the files instantly."],
              ["Can I use these for print-on-demand?",
               "Yes. The commercial license covers unlimited products on Printful, Printify, Redbubble, Merch by Amazon, Etsy and your own store. No royalties."],
              ["Do I need design software?",
               "No. PNG files upload directly to any POD platform or print shop. No Photoshop needed. EPS, AI, SVG and PSD files are included for designers who want to edit."],
              ["What is the refund policy?",
               "Because this is an instant digital download, refunds are not available once the download link has been accessed. Technical issues with delivery are resolved immediately."],
              ["Is there a selling limit?",
               "No limits at all. Sell on as many platforms as you like, in any volume. The license covers unlimited commercial use."],
            ].map(([q, a]) => (
              <div key={q} className="faq-item">
                <button className="faq-q">{q}<span className="faq-plus">+</span></button>
                <div className="faq-a">{a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="s-section s-section--flush-top">
        <div className="s-wrap">
          <div className="s-cta">
            <div className="s-cta__glow" />
            <h2 className="s-cta__h">Stop designing from scratch.<br />Start selling today.</h2>
            <p className="s-cta__sub">50,000+ print-ready designs. ₹299 one-time. In your inbox in seconds.</p>
            <button className="s-btn s-btn--gold s-btn--xl" onClick={handleBuy}>Get Instant Access - ₹299</button>
          </div>
        </div>
      </section>

      {/* SUPPORT */}
      <section className="s-section s-section--flush-top">
        <div className="s-wrap">
          <div className="support-box">
            <div className="support-left">
              <p className="s-eyebrow">Support</p>
              <h2 className="s-h2">Need help?</h2>
              <p className="support-desc">Paid but didn't receive your files? Something not working? We respond within a few hours.</p>
              <a href="mailto:mail@brandorah.com" className="support-mail">mail@brandorah.com</a>
            </div>
            <div className="support-right">
              <SupportForm />
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="s-footer">
        <div className="s-wrap">

          {/* Policy rows */}
          <div className="s-footer__policies">
            <div className="s-footer__policy">
              <p className="s-footer__policy-title">Refund Policy</p>
              <p className="s-footer__policy-text">This is a digital product. Once the download link has been accessed, refunds are not available. If you face any technical issue with delivery, contact us at mail@brandorah.com and we will resolve it immediately.</p>
            </div>
            <div className="s-footer__policy">
              <p className="s-footer__policy-title">Privacy Policy</p>
              <p className="s-footer__policy-text">We collect only your name and email address to deliver your purchase and send the download link. We do not sell or share your data with any third party. Your payment is processed securely by Razorpay and we do not store card details.</p>
            </div>
            <div className="s-footer__policy">
              <p className="s-footer__policy-title">License</p>
              <p className="s-footer__policy-text">All designs are licensed for unlimited commercial use. You may sell on any print-on-demand platform, in any volume, with no royalties. Reselling the raw design files as a bundle is not permitted.</p>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="s-footer__inner">
            <span>© 2026 Brandorah</span>
            <div className="s-footer__links">
              <a href="mailto:mail@brandorah.com">mail@brandorah.com</a>
              <a href="mailto:mail@brandorah.com">Contact</a>
            </div>
            <span>Secured by Razorpay</span>
          </div>

        </div>
      </footer>

    </div>
  );
}
